import React, { useEffect, useRef, useState } from 'react';

const UPLOADS_URL = (process.env.REACT_APP_UPLOADS_URL || 'https://uploads.pastewaves.com').replace(/\/+$/, '');
const BAR_COUNT = 180;

const cache = new Map<string, number[]>();
const inflight = new Map<string, Promise<number[] | null>>();

const downsample = (data: number[], target: number): number[] => {
  if (data.length <= target) return data;
  const out: number[] = new Array(target);
  const block = data.length / target;
  for (let i = 0; i < target; i++) {
    const start = Math.floor(i * block);
    const end = Math.floor((i + 1) * block);
    let max = 0;
    for (let j = start; j < end; j++) {
      const v = Math.abs(data[j]);
      if (v > max) max = v;
    }
    out[i] = max;
  }
  return out;
};

const fetchWaveform = async (fileId: string): Promise<number[] | null> => {
  if (cache.has(fileId)) return cache.get(fileId)!;
  if (inflight.has(fileId)) return inflight.get(fileId)!;

  const promise = (async () => {
    try {
      const url = `${UPLOADS_URL}/uploads/${fileId}/waveform.csv`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const text = await res.text();
      const lines = text.trim().split('\n').slice(1);
      const amps: number[] = [];
      for (const line of lines) {
        const parts = line.split(',');
        if (parts.length >= 2) {
          const a = parseFloat(parts[1]);
          if (!isNaN(a)) amps.push(a);
        }
      }
      if (amps.length === 0) return null;
      const reduced = downsample(amps, BAR_COUNT);
      const max = Math.max(...reduced);
      const normalized = max > 0 ? reduced.map(v => v / max) : reduced;
      cache.set(fileId, normalized);
      return normalized;
    } catch {
      return null;
    } finally {
      inflight.delete(fileId);
    }
  })();

  inflight.set(fileId, promise);
  return promise;
};

interface MiniWaveformProps {
  fileId: string;
  progress?: number;
  isActive?: boolean;
  height?: number;
  onSeek?: (progress: number) => void;
  /** 'dark' (default) paints unplayed bars as white-on-transparent for
   *  dark surfaces; 'light' swaps to black-on-transparent for the light
   *  embed theme. Played overlay stays soundwave-orange either way — it
   *  reads on both. */
  theme?: 'dark' | 'light';
}

const MiniWaveform: React.FC<MiniWaveformProps> = ({
  fileId,
  progress = 0,
  isActive = false,
  height = 32,
  onSeek,
  theme = 'dark',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [data, setData] = useState<number[] | null>(() => cache.get(fileId) ?? null);

  useEffect(() => {
    let cancelled = false;
    if (cache.has(fileId)) {
      setData(cache.get(fileId)!);
      return;
    }
    fetchWaveform(fileId).then(result => {
      if (!cancelled) setData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    if (cssWidth === 0) return;

    if (canvas.width !== Math.floor(cssWidth * dpr) || canvas.height !== Math.floor(height * dpr)) {
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, height);

    const samples = data ?? new Array(BAR_COUNT).fill(0).map(() => 0.15 + Math.random() * 0.25);
    const isPlaceholder = !data;

    const clampedProgress = Math.max(0, Math.min(1, progress));
    const playheadX = clampedProgress * cssWidth;
    const mid = height / 2;
    const amplitude = height * 0.42;

    // Build smooth silhouette: top edge with quadratic curves, mirrored on bottom.
    const points: Array<[number, number]> = samples.map((v, i) => {
      const x = (i / (samples.length - 1)) * cssWidth;
      const y = Math.max(0.04, v) * amplitude;
      return [x, y];
    });

    const path = new Path2D();
    path.moveTo(0, mid);
    // Top edge
    for (let i = 0; i < points.length - 1; i++) {
      const [x1, y1] = points[i];
      const [x2, y2] = points[i + 1];
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      path.quadraticCurveTo(x1, mid - y1, cx, mid - cy);
    }
    const lastTop = points[points.length - 1];
    path.lineTo(lastTop[0], mid - lastTop[1]);
    path.lineTo(cssWidth, mid);
    // Bottom edge (mirrored)
    path.lineTo(lastTop[0], mid + lastTop[1]);
    for (let i = points.length - 1; i > 0; i--) {
      const [x1, y1] = points[i];
      const [x2, y2] = points[i - 1];
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      path.quadraticCurveTo(x1, mid + y1, cx, mid + cy);
    }
    path.closePath();

    // Base fill (unplayed) — inverted for light theme so bars remain
    // visible against a light card surface. Alpha stops match the dark
    // treatment so the visual weight is proportional.
    const isLight = theme === 'light';
    if (isPlaceholder) {
      ctx.fillStyle = isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.04)';
    } else if (isActive) {
      ctx.fillStyle = isLight ? 'rgba(0, 0, 0, 0.24)' : 'rgba(255, 255, 255, 0.16)';
    } else {
      ctx.fillStyle = isLight ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.10)';
    }
    ctx.fill(path);

    // Played overlay
    if (isActive && data && clampedProgress > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, playheadX, height);
      ctx.clip();
      const grad = ctx.createLinearGradient(0, 0, playheadX, 0);
      grad.addColorStop(0, 'rgba(255, 110, 40, 0.35)');
      grad.addColorStop(1, 'rgba(255, 110, 40, 0.55)');
      ctx.fillStyle = grad;
      ctx.fill(path);
      ctx.restore();
    }

    // Playhead line
    if (isActive && data && clampedProgress > 0 && clampedProgress < 1) {
      const haloGrad = ctx.createLinearGradient(playheadX - 6, 0, playheadX + 6, 0);
      haloGrad.addColorStop(0, 'rgba(255, 110, 40, 0)');
      haloGrad.addColorStop(0.5, 'rgba(255, 160, 80, 0.18)');
      haloGrad.addColorStop(1, 'rgba(255, 110, 40, 0)');
      ctx.fillStyle = haloGrad;
      ctx.fillRect(playheadX - 6, 0, 12, height);

      ctx.fillStyle = 'rgba(255, 220, 180, 0.55)';
      ctx.fillRect(playheadX - 0.5, 0, 1, height);
    }
  }, [data, progress, isActive, height, theme]);

  // Pointer-based scrubbing: pointerdown captures the canvas so a drag
  // that leaves the element still fires pointermove; a bare tap without
  // drag still fires a single seek (pointerdown + pointerup at the same
  // x). Replaces the click-only handler so the waveform is scrubbable
  // (drag-to-scan) in addition to seekable (tap-to-jump). `touch-action:
  // none` on the canvas keeps a mobile drag from being interpreted as
  // scroll and interrupting the scrub.
  const draggingRef = useRef(false);

  const seekAtClientX = (clientX: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !onSeek) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    const next = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(next);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!onSeek) return;
    e.stopPropagation();
    draggingRef.current = true;
    const canvas = canvasRef.current;
    if (canvas) {
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Capture can throw if the pointer id is stale; ignore and continue
        // — the drag still works, we just lose out-of-bounds tracking.
      }
    }
    seekAtClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    seekAtClientX(e.clientX);
  };

  const releaseDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={releaseDrag}
      onPointerCancel={releaseDrag}
      style={{
        width: '100%',
        height: `${height}px`,
        display: 'block',
        cursor: onSeek ? 'pointer' : 'default',
        touchAction: onSeek ? 'none' : 'auto',
      }}
      aria-hidden="true"
    />
  );
};

export default React.memo(MiniWaveform);
